import { useCallback } from 'react';
import {
    StructureSetCopyService,
    CopyStructureSetResult,
    SdfContourResolutionStrategy,
} from '~/domain/services/structureSetService/structureSetCopyService';
import { StructureSet } from '~/domain/patient/structureset';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { useRecoilValue } from 'recoil';
import { environmentState } from '~/state/atoms/engineAtoms';

export function useCopyStructureSet() {
    const environment = useRecoilValue(environmentState);

    const canCopy = useCallback(
        (
            sourceStructureSet: StructureSet | null | undefined,
            targetImage: Image3D | null | undefined
        ): boolean => {
            if (!sourceStructureSet || !targetImage) {
                return false;
            }
            if (!sourceStructureSet.image) {
                return false;
            }
            if (!targetImage.frameOfReferenceUid) {
                return false;
            }
            if (sourceStructureSet.image.seriesUid === targetImage.seriesUid) {
                return false;
            }
            return true;
        },
        []
    );

    const copyStructureSet = useCallback(
        async (
            sourceStructureSet: StructureSet,
            targetImage: Image3D,
            patient: Patient,
            selectedRoiNumbers?: Set<number>
        ): Promise<CopyStructureSetResult> => {
            if (!sourceStructureSet.image) {
                throw new Error(
                    'Source structure set is not linked to an image. ' +
                        'Cannot determine geometry for copying.'
                );
            }

            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set.'
                );
            }

            environment.saveChanges();

            const service = new StructureSetCopyService();
            const result = service.copySameGeometry({
                sourceStructureSet,
                targetImage,
                existingStructureSets: patient.structureSets,
                selectedRoiNumbers,
            });

            patient.addStructureSet(result.structureSet);
            return result;
        },
        [environment]
    );

    const copyIntoExisting = useCallback(
        async (
            sourceStructureSet: StructureSet,
            targetStructureSet: StructureSet,
            selectedRoiNumbers: Set<number>,
            overwriteExisting: boolean = false
        ): Promise<CopyStructureSetResult> => {
            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set.'
                );
            }

            environment.saveChanges();

            const service = new StructureSetCopyService();
            const result = service.copyIntoExistingStructureSet({
                sourceStructureSet,
                targetStructureSet,
                selectedRoiNumbers,
                overwriteExisting,
            });

            return {
                structureSet: targetStructureSet,
                copiedRoiNumbers: result.copiedRoiNumbers,
                skippedRois: result.skippedRois,
            };
        },
        [environment]
    );

    const copyWithRegistration = useCallback(
        async (
            sourceStructureSet: StructureSet,
            targetImage: Image3D,
            registration: RigidRegistration,
            patient: Patient,
            selectedRoiNumbers?: Set<number>
        ): Promise<CopyStructureSetResult> => {
            if (!sourceStructureSet.image) {
                throw new Error(
                    'Source structure set is not linked to an image. ' +
                        'Cannot determine geometry for copying.'
                );
            }

            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set with registration.'
                );
            }

            environment.saveChanges();

            const service = new StructureSetCopyService();
            const strategy = new SdfContourResolutionStrategy(environment);

            const result = await service.copyWithRegistration(
                {
                    sourceStructureSet,
                    targetImage,
                    registration,
                    existingStructureSets: patient.structureSets,
                    selectedRoiNumbers,
                },
                strategy
            );

            patient.addStructureSet(result.structureSet);
            return result;
        },
        [environment]
    );

    const copyWithRegistrationIntoExisting = useCallback(
        async (
            sourceStructureSet: StructureSet,
            targetStructureSet: StructureSet,
            targetImage: Image3D,
            registration: RigidRegistration,
            selectedRoiNumbers: Set<number>,
            overwriteExisting: boolean = false
        ): Promise<CopyStructureSetResult> => {
            if (!sourceStructureSet.image) {
                throw new Error(
                    'Source structure set is not linked to an image. ' +
                        'Cannot determine geometry for copying.'
                );
            }

            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set with registration into existing.'
                );
            }

            if (
                sourceStructureSet.approvalStatus !==
                DICOMApprovalStatus.APPROVED
            ) {
                environment.saveChanges();
            }

            const service = new StructureSetCopyService();
            const strategy = new SdfContourResolutionStrategy(environment);

            return service.copyWithRegistrationIntoExisting(
                {
                    sourceStructureSet,
                    targetStructureSet,
                    targetImage,
                    registration,
                    selectedRoiNumbers,
                    overwriteExisting,
                },
                strategy
            );
        },
        [environment]
    );

    const copySameFrameOfReference = useCallback(
        async (
            sourceStructureSet: StructureSet,
            targetImage: Image3D,
            patient: Patient,
            selectedRoiNumbers?: Set<number>
        ): Promise<CopyStructureSetResult> => {
            if (!sourceStructureSet.image) {
                throw new Error(
                    'Source structure set is not linked to an image. ' +
                        'Cannot determine geometry for copying.'
                );
            }

            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set.'
                );
            }

            environment.saveChanges();

            const service = new StructureSetCopyService();
            const strategy = new SdfContourResolutionStrategy(environment);

            const result = await service.copySameFrameOfReference(
                {
                    sourceStructureSet,
                    targetImage,
                    existingStructureSets: patient.structureSets,
                    selectedRoiNumbers,
                },
                strategy
            );

            patient.addStructureSet(result.structureSet);
            return result;
        },
        [environment]
    );

    const copySameFrameOfReferenceIntoExisting = useCallback(
        async (
            sourceStructureSet: StructureSet,
            targetStructureSet: StructureSet,
            targetImage: Image3D,
            selectedRoiNumbers: Set<number>,
            overwriteExisting: boolean = false
        ): Promise<CopyStructureSetResult> => {
            if (!sourceStructureSet.image) {
                throw new Error(
                    'Source structure set is not linked to an image. ' +
                        'Cannot determine geometry for copying.'
                );
            }

            if (!environment) {
                throw new Error(
                    'Environment is not available for copying structure set.'
                );
            }

            if (
                sourceStructureSet.approvalStatus !==
                DICOMApprovalStatus.APPROVED
            ) {
                environment.saveChanges();
            }

            const service = new StructureSetCopyService();
            const strategy = new SdfContourResolutionStrategy(environment);

            return service.copySameFrameOfReferenceIntoExisting(
                {
                    sourceStructureSet,
                    targetStructureSet,
                    targetImage,
                    selectedRoiNumbers,
                    overwriteExisting,
                },
                strategy
            );
        },
        [environment]
    );

    return {
        copyStructureSet,
        copyIntoExisting,
        copyWithRegistration,
        copyWithRegistrationIntoExisting,
        copySameFrameOfReference,
        copySameFrameOfReferenceIntoExisting,
        canCopy,
    };
}
